/**
 * Suggest agents — a grouping for a solution built without groups.
 *
 * Real solutions have hundreds or thousands of nodes and no explicit groups,
 * but the structure is there, written three ways: node names ("Orders -
 * Manage Return"), node numbers (5000, 50102, 502010 all hang off 50), and
 * routing (what points where). This reads all three, in that order of
 * trust:
 *
 * 1. Names seed the groups. A name's topic is the text before its first
 *    separator; a topic three or more nodes share is a group. Topics that
 *    are really a command's name ("SetVar - …") say what a node does, not
 *    what it's for, so they don't count.
 * 2. Routing spreads each group to the unnamed nodes around it: a node takes
 *    the group most of its neighbours are in. Hubs — nodes most of the
 *    solution routes into, like an error node or escalation — would join
 *    everything together, so nothing spreads through them.
 * 3. Numbers fill in what's left: a node joins the group that the nodes
 *    sharing the longest start of its number are mostly in.
 *
 * Where names say little (under a third of nodes), the main menu's branches
 * seed groups too, named by their button labels.
 *
 * Plain code — the same answer every time, instant on 4,000 nodes. What it
 * can't name well it says so (`needsName`), for AI to name.
 *
 * Pure: rows in, a suggestion out. Nothing is changed until the designer
 * accepts it.
 */

export interface GroupingRow {
  id: string;
  nodeNumber?: unknown;
  nodeName?: unknown;
  nodeType?: unknown;
  command?: unknown;
  nextNodes?: unknown;
  whatsNext?: unknown;
  richAssetContent?: unknown;
}

export type AgentNameSource = 'names' | 'menu';

export interface SuggestedAgent {
  /** Stable within one suggestion. */
  key: string;
  name: string;
  /** Where the name came from: node names, or the main menu's buttons. */
  nameSource: AgentNameSource;
  /** True when the name is a guess worth asking AI to improve. */
  needsName: boolean;
  rowIds: string[];
  /** How many came from their own name, not routing or numbers. */
  namedCount: number;
}

export interface AgentSuggestion {
  agents: SuggestedAgent[];
  /** Hubs and reserved nodes every agent uses — error handling, escalation, the end. */
  shared: string[];
  /** Nodes nothing placed. */
  leftover: string[];
}

/** Fewer nodes than this doesn't make a topic. */
const MIN_AGENT = 3;
/** A group this small that nothing folded in isn't worth an agent. */
const MIN_SUGGESTED = 6;
/** Groups smaller than this fold into the group they route with most. */
const SMALL_AGENT = 10;
const SPREAD_STEPS = 4;

const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

const key = (v: unknown): string => {
  const raw = String(v ?? '')
    .replace(/^'/, '')
    .trim();
  if (!raw) return '';
  const n = Number(raw);
  return Number.isFinite(n) ? String(n) : '';
};

const items = (v: unknown): string[] =>
  (Array.isArray(v)
    ? v.map((x) => String(x ?? ''))
    : typeof v === 'string'
      ? v.split(/[|,;\n]/)
      : typeof v === 'number'
        ? [String(v)]
        : []
  )
    .map((s) => s.trim())
    .filter(Boolean);

/** `label~node` pairs, from whatsNext and button-style rich assets. */
const pairs = (v: unknown) =>
  items(v)
    .filter((s) => s.includes('~'))
    .map((s) => {
      const i = s.lastIndexOf('~');
      return { label: s.slice(0, i).trim(), dest: key(s.slice(i + 1)) };
    })
    .filter((p) => p.dest);

/** The part of a name before its first separator: "Orders - Manage" → "Orders". */
export const nameTopic = (name: string): string | null => {
  const m = /^\s*([^-–:|>]{2,40}?)\s*(?:-|–|:|\||>)\s*\S/.exec(name);
  return m ? m[1].trim() : null;
};

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '');

/** Topics that describe how a node works, not what it's for. */
const TECHNICAL_TOPICS = new Set(
  [
    'setvar',
    'varcheck',
    'getvalue',
    'nlu',
    'api',
    'sys',
    'action',
    'decision',
    'node',
    'test',
    'debug',
    'todo',
    'copy',
    'new',
    're',
  ].map(norm)
);

/** "LookupFAQ", "In", "BK?" — a label, not a name a designer would pick. */
const looksLikeCode = (name: string) =>
  name.length < 3 || /[?_]/.test(name) || /^[A-Z]?[a-z]+[A-Z]\w*$/.test(name);

/** "orders" → "Orders", "SPANISH" → "Spanish"; acronyms and mixed case stay. */
const titleCase = (s: string) => {
  if (s === s.toLowerCase()) return s.charAt(0).toUpperCase() + s.slice(1);
  if (/^[A-Z]{6,}$/.test(s)) return s.charAt(0) + s.slice(1).toLowerCase();
  return s;
};

interface Node {
  row: GroupingRow;
  key: string;
  name: string;
  out: string[];
  /** Button labels leading to each destination, for naming menu branches. */
  labels: Map<string, string>;
}

/** What every step works on: the nodes, their routes, and the labels so far. */
interface Grouping {
  nodes: Map<string, Node>;
  /** Node → the nodes that route to it. */
  inc: Map<string, string[]>;
  shared: Set<string>;
  /** Node → group id. */
  label: Map<string, string>;
  /** Group id → display name. */
  names: Map<string, string>;
}

const readNodes = (rows: GroupingRow[]) => {
  const nodes = new Map<string, Node>();
  rows.forEach((row) => {
    const k = key(row.nodeNumber);
    if (!k || nodes.has(k)) return;
    const labels = new Map<string, string>();
    const out = new Set<string>();
    items(row.nextNodes).forEach((s) => {
      const d = key(s);
      if (d) out.add(d);
    });
    pairs(row.whatsNext).forEach((p) => out.add(p.dest));
    pairs(row.richAssetContent).forEach((p) => {
      out.add(p.dest);
      if (p.label && !labels.has(p.dest)) labels.set(p.dest, p.label);
    });
    out.delete(k);
    nodes.set(k, {
      row,
      key: k,
      name: text(row.nodeName),
      out: [...out],
      labels,
    });
  });
  // Routes to nodes that don't exist go nowhere.
  nodes.forEach((n) => {
    n.out = n.out.filter((d) => nodes.has(d));
  });
  return nodes;
};

const incoming = (nodes: Map<string, Node>) => {
  const inc = new Map<string, string[]>();
  nodes.forEach((n) =>
    n.out.forEach((d) => inc.set(d, [...(inc.get(d) ?? []), n.key]))
  );
  return inc;
};

/**
 * Nodes the platform reserves (negative numbers, like -500), and hubs: nodes
 * routed into from far more places than a step in one flow would be.
 */
const findShared = (nodes: Map<string, Node>, inc: Map<string, string[]>) => {
  const hubAt = Math.max(8, Math.ceil(nodes.size * 0.02));
  const shared = new Set<string>();
  nodes.forEach((n) => {
    if (Number(n.key) < 0 || (inc.get(n.key)?.length ?? 0) >= hubAt) {
      shared.add(n.key);
    }
  });
  return shared;
};

/** Words that head a section rather than name one: "TOPIC: Working with dates". */
const HEADER_TOPICS = new Set(
  ['topic', 'section', 'module', 'flow', 'use case', 'intent'].map(norm)
);

/**
 * A name's topics, outermost first: "SPANISH: Employee info - Name" →
 * ["SPANISH", "Employee info"]. A header word is skipped for what follows it.
 */
export const topicPath = (name: string): string[] => {
  const path: string[] = [];
  let rest = name;
  let header = false;
  while (path.length < 2) {
    const t = nameTopic(rest);
    if (!t) {
      if (header && !path.length && rest.trim()) path.push(rest.trim());
      break;
    }
    rest = rest
      .slice(rest.indexOf(t) + t.length)
      .replace(/^\s*(?:-|–|:|\||>)\s*/, '');
    if (!path.length && !header && HEADER_TOPICS.has(norm(t))) {
      header = true;
      continue;
    }
    path.push(t);
  }
  return path;
};

interface Topic {
  keys: string[];
  spellings: Map<string, number>;
}

const addTo = (
  map: Map<string, Topic>,
  t: string,
  node: { raw: string; key: string }
) => {
  const entry: Topic = map.get(t) ?? { keys: [], spellings: new Map() };
  const { raw, key: k } = node;
  entry.keys.push(k);
  entry.spellings.set(raw, (entry.spellings.get(raw) ?? 0) + 1);
  map.set(t, entry);
};

const spelling = (entry: Topic) =>
  titleCase([...entry.spellings].sort((a, b) => b[1] - a[1])[0][0]);

/** Step 1: topics from names. Returns node → topic id, and each topic's display name. */
const seedFromNames = (nodes: Map<string, Node>) => {
  const commands = new Set<string>();
  nodes.forEach((n) => {
    const c = norm(text(n.row.command));
    if (c) commands.add(c);
  });
  const technical = (t: string) =>
    !t ||
    TECHNICAL_TOPICS.has(t) ||
    commands.has(t) ||
    commands.has(`sys${t}`) ||
    /^\d+$/.test(t);

  const byTopic = new Map<string, Topic>();
  /** Names repeated whole, with no topic in them: "Handover to store" × 300. */
  const byWholeName = new Map<string, Topic>();
  const subOf = new Map<string, { t: string; raw: string }>();
  nodes.forEach((n) => {
    const [raw, sub] = topicPath(n.name);
    if (!raw) {
      if (n.name)
        addTo(byWholeName, `name:${norm(n.name)}`, { raw: n.name, key: n.key });
      return;
    }
    const t = norm(raw);
    if (technical(t)) return;
    addTo(byTopic, t, { raw, key: n.key });
    if (sub && !technical(norm(sub)))
      subOf.set(n.key, { t: norm(sub), raw: sub });
  });

  // "LookupFAQ - Billing" on a LookupFAQ node names the command, not a topic.
  const echoesCommand = (t: string, keys: string[]) =>
    keys.filter((k) => {
      const c = norm(text(nodes.get(k)?.row.command));
      return c && (c.includes(t) || t.includes(c));
    }).length >=
    keys.length / 2;

  const label = new Map<string, string>();
  const names = new Map<string, string>();
  /** A split layer's nodes with no topic of their own: placed by routing first, the layer last. */
  const layerOf = new Map<string, string>();
  byTopic.forEach((entry, t) => {
    if (entry.keys.length < MIN_AGENT || echoesCommand(t, entry.keys)) return;
    names.set(t, spelling(entry));
    entry.keys.forEach((k) => label.set(k, t));

    // A topic holding a quarter of the solution is a layer, not an agent —
    // a language copy of the whole bot, say. Split it by its own topics.
    if (entry.keys.length < nodes.size / 4) return;
    const subs = new Map<string, Topic>();
    entry.keys.forEach((k) => {
      const sub = subOf.get(k);
      if (sub) addTo(subs, `${t}/${sub.t}`, { raw: sub.raw, key: k });
    });
    const kept = new Set<string>();
    subs.forEach((subEntry, id) => {
      if (subEntry.keys.length < MIN_AGENT) return;
      names.set(id, `${names.get(t)} · ${spelling(subEntry)}`);
      subEntry.keys.forEach((k) => {
        label.set(k, id);
        kept.add(k);
      });
    });
    if (!kept.size) return;
    entry.keys.forEach((k) => {
      if (kept.has(k)) return;
      label.delete(k);
      layerOf.set(k, t);
    });
  });
  byWholeName.forEach((entry, t) => {
    if (entry.keys.length < SMALL_AGENT) return;
    if (echoesCommand(norm(t.slice(5)), entry.keys)) return;
    names.set(t, spelling(entry));
    entry.keys.forEach((k) => label.set(k, t));
  });
  return { label, names, layerOf };
};

/**
 * The main menu: from the start, the first node offering two or more buttons
 * that lead somewhere. Its branches not already in a topic become groups,
 * named by their buttons.
 */
const seedFromMenu = ({ nodes, inc, shared, label, names }: Grouping) => {
  const start =
    nodes.get('1') ??
    [...nodes.values()]
      .filter((n) => Number(n.key) > 0 && !(inc.get(n.key)?.length ?? 0))
      .sort((a, b) => Number(a.key) - Number(b.key))[0];
  if (!start) return;

  const seen = new Set([start.key]);
  const queue = [start];
  while (queue.length) {
    const n = queue.shift() as Node;
    const branches = n.out.filter((d) => !shared.has(d));
    const buttons = branches.filter((d) => n.labels.has(d));
    if (buttons.length >= 2) {
      buttons.forEach((d) => {
        if (label.has(d)) return;
        const id = `menu:${d}`;
        const name = n.labels.get(d) as string;
        label.set(d, id);
        names.set(id, titleCase(name.replace(/^[^\p{L}\p{N}]+/u, '').trim()));
      });
      return;
    }
    branches.forEach((d) => {
      if (!seen.has(d)) {
        seen.add(d);
        queue.push(nodes.get(d) as Node);
      }
    });
  }
};

/**
 * Step 2: spread labels along routes, never through a hub, and only a few
 * steps from a named node — further than that, one group floods everything
 * it reaches first.
 */
const spread = ({ nodes, inc, shared, label }: Grouping, steps: number) => {
  for (let pass = 0; pass < steps; pass += 1) {
    const next = new Map<string, string>();
    nodes.forEach((n) => {
      if (label.has(n.key) || shared.has(n.key)) return;
      const votes = new Map<string, number>();
      const vote = (k: string, w: number) => {
        if (shared.has(k)) return;
        const l = label.get(k);
        if (l) votes.set(l, (votes.get(l) ?? 0) + w);
      };
      // What leads here says more about a node than where it goes.
      (inc.get(n.key) ?? []).forEach((k) => vote(k, 2));
      n.out.forEach((k) => vote(k, 1));
      const total = [...votes.values()].reduce((a, b) => a + b, 0);
      const [best] = [...votes].sort((a, b) => b[1] - a[1]);
      if (best && best[1] / total > 0.5) next.set(n.key, best[0]);
    });
    if (!next.size) return;
    next.forEach((l, k) => label.set(k, l));
  }
};

/** Step 3: the group the nodes sharing the longest start of this number mostly belong to. */
const fillFromNumbers = ({ nodes, shared, label }: Grouping) => {
  const byPrefix = new Map<string, Map<string, number>>();
  label.forEach((l, k) => {
    if (!/^\d+$/.test(k)) return;
    for (let len = 2; len <= k.length; len += 1) {
      const p = k.slice(0, len);
      const counts = byPrefix.get(p) ?? new Map<string, number>();
      counts.set(l, (counts.get(l) ?? 0) + 1);
      byPrefix.set(p, counts);
    }
  });
  const fill = new Map<string, string>();
  nodes.forEach((n) => {
    if (label.has(n.key) || shared.has(n.key) || !/^\d+$/.test(n.key)) return;
    for (let len = n.key.length - 1; len >= 2; len -= 1) {
      const counts = byPrefix.get(n.key.slice(0, len));
      if (!counts) continue;
      const total = [...counts.values()].reduce((a, b) => a + b, 0);
      const [best] = [...counts].sort((a, b) => b[1] - a[1]);
      if (total >= MIN_AGENT && best[1] / total >= 0.75) {
        fill.set(n.key, best[0]);
        break;
      }
    }
  });
  fill.forEach((l, k) => label.set(k, l));
};

/**
 * Step 4: a small group that mostly routes to and from one other group is a
 * part of it — "Shoulder", "Arm", "Knee" inside an injury flow — so it folds
 * in, smallest first. One that routes all over stays its own.
 */
const foldSmallGroups = ({ nodes, inc, shared, label, names }: Grouping) => {
  const smallAt = Math.max(SMALL_AGENT, Math.ceil(nodes.size * 0.01));
  for (let changed = true; changed; ) {
    changed = false;
    const size = new Map<string, number>();
    label.forEach((l) => size.set(l, (size.get(l) ?? 0) + 1));
    const small = [...size]
      .filter(([, n]) => n < smallAt)
      .sort((a, b) => a[1] - b[1]);
    for (const [group] of small) {
      const links = new Map<string, number>();
      label.forEach((l, k) => {
        if (l !== group) return;
        const node = nodes.get(k) as Node;
        [...node.out, ...(inc.get(k) ?? [])].forEach((o) => {
          const ol = label.get(o);
          if (ol && ol !== group && !shared.has(o)) {
            links.set(ol, (links.get(ol) ?? 0) + 1);
          }
        });
      });
      const total = [...links.values()].reduce((a, b) => a + b, 0);
      const [best] = [...links].sort((a, b) => b[1] - a[1]);
      if (best && best[1] / total >= 0.6) {
        label.forEach((l, k) => {
          if (l === group) label.set(k, best[0]);
        });
        names.delete(group);
        changed = true;
        break;
      }
    }
  }
};

/**
 * A topic that IS one of the two agents every solution has — "Supervisor",
 * "Knowledge Agent" — gets that agent's exact name, which is what makes the
 * canvas file it under Core rather than as a use-case agent.
 */
const CORE_NAMES: Record<string, string> = {
  supervisor: 'Supervisor Agent',
  supervisoragent: 'Supervisor Agent',
  knowledge: 'Knowledge Agent',
  knowledgeagent: 'Knowledge Agent',
};
const coreName = (name: string) => CORE_NAMES[norm(name)] ?? name;

export const suggestAgents = (rows: GroupingRow[]): AgentSuggestion => {
  const nodes = readNodes(rows);
  const inc = incoming(nodes);
  const shared = findShared(nodes, inc);

  const { label, names, layerOf } = seedFromNames(nodes);
  const g: Grouping = { nodes, inc, shared, label, names };
  const named = new Set([...label.keys(), ...layerOf.keys()]);
  // Where names say little, the menu's branches are the only seeds, with
  // nothing to flood — so they spread as far as routing goes.
  const sparse = named.size < nodes.size / 3;
  if (sparse) seedFromMenu(g);
  // A hub keeps its own name's topic but nothing else.
  spread(g, sparse ? 50 : SPREAD_STEPS);
  fillFromNumbers(g);
  layerOf.forEach((t, k) => {
    if (!label.has(k)) label.set(k, t);
  });
  // Two topics can read the same ("Handover to store" as a topic and as a whole name).
  const byName = new Map<string, string>();
  label.forEach((l, k) => {
    const n = norm(names.get(l) ?? l);
    const first = byName.get(n) ?? l;
    byName.set(n, first);
    if (first !== l) label.set(k, first);
  });

  foldSmallGroups(g);

  const members = new Map<string, string[]>();
  label.forEach((l, k) => {
    if (shared.has(k) && !named.has(k)) return;
    members.set(l, [...(members.get(l) ?? []), k]);
  });

  const placed = new Set<string>();
  const agents: SuggestedAgent[] = [...members]
    .filter(([, keys]) => keys.length >= MIN_SUGGESTED)
    .map(([id, keys]) => {
      keys.forEach((k) => placed.add(k));
      const sorted = keys.sort((a, b) => Number(a) - Number(b));
      const source: AgentNameSource = id.startsWith('menu:') ? 'menu' : 'names';
      const name = coreName(
        names.get(id) ?? `Nodes ${sorted[0]}–${sorted.at(-1)}`
      );
      return {
        key: id,
        name,
        nameSource: source,
        needsName: source !== 'names' || looksLikeCode(name),
        rowIds: sorted.map((k) => (nodes.get(k) as Node).row.id),
        namedCount: sorted.filter((k) => named.has(k)).length,
        first: Number(sorted[0]),
      };
    })
    .sort((a, b) => a.first - b.first)
    .map(({ first: _first, ...agent }) => agent);

  const sharedIds: string[] = [];
  const leftover: string[] = [];
  nodes.forEach((n) => {
    if (placed.has(n.key)) return;
    (shared.has(n.key) ? sharedIds : leftover).push(n.row.id);
  });
  return { agents, shared: sharedIds, leftover };
};
